import React from 'react';
import { Box, Button, Divider, FormControl, FormControlLabel, InputLabel, MenuItem, Select, Stack, TextField } from '@mui/material';
import { theme } from '../../../../../../../common/styles/theme';
import { Check } from '../../../../../../../common/components/Check/Check';
import { Text } from '../../../../../../../common/components/Text/Text';
import { TBookSettings } from '../../../../../../../common/rest/endlessBook/endlessBookRest';
import { FICTION_GENRES, NONFICTION_CATEGORIES } from '../constants';
import { BookVoiceSettingsHOC } from '../../BookVoiceSettings/hocs/BookVoiceSettingsHOC';
import { BookLangSelect } from './BookLangSelect';
import { IBookSettingsFormProps } from './types';

// The book's settings: narration voice (saved at once) and the generation settings (applied by regenerating).
export const BookSettingsForm: React.FC<IBookSettingsFormProps> = ({
	settings,
	regenerating,
	langOptions,
	onRegenerate,
	onChangeTranslationLang,
}) => {
	const [form, setForm] = React.useState<TBookSettings | undefined>(settings);
	// New saved settings reset the form — except a translation-language change, which isn't part of it.
	const prevSettingsRef = React.useRef(settings);
	// keywords typed per book language while the form is open
	const keywordsByLangRef = React.useRef<Record<string, string>>({});
	React.useEffect(() => {
		const prev = prevSettingsRef.current;
		prevSettingsRef.current = settings;
		const onlyNativeLang = prev && settings && JSON.stringify({ ...prev, nativeLang: '' }) === JSON.stringify({ ...settings, nativeLang: '' });
		if (onlyNativeLang) return;
		setForm(settings);
		keywordsByLangRef.current = {};
	}, [settings]);
	const [plotExpanded, setPlotExpanded] = React.useState(false);

	if (!form || !settings) return null;

	const set = (patch: Partial<TBookSettings>) => setForm({ ...form, ...patch });
	const genres = form.nonfiction ? NONFICTION_CATEGORIES : FICTION_GENRES;
	// a genre the lists don't know (set elsewhere) stays selectable
	const genreOptions = genres.includes(form.genre) || !form.genre ? genres : [form.genre, ...genres];

	// Keywords are words of the book's language: endless-book saves them to the reader's vocabulary
	// under it. Each language keeps its own keywords while the form is open; a new one starts empty.
	const onChangeTargetLang = (targetLang: string) => {
		if (targetLang === form.targetLang) return;
		const byLang = keywordsByLangRef.current;
		byLang[form.targetLang] = form.keywords;
		set({
			targetLang,
			keywords: byLang[targetLang] ?? (targetLang === settings.targetLang ? settings.keywords : ''),
		});
	};
	const keywordsDropped = form.targetLang !== settings.targetLang && !!settings.keywords && !form.keywords;

	const onToggleNonfiction = () => {
		const nonfiction = !form.nonfiction;
		set({ nonfiction, genre: (nonfiction ? NONFICTION_CATEGORIES : FICTION_GENRES)[0] });
	};

	return (
		<Stack gap={3}>
			{/* applied at once, the text stays */}
			<Stack direction={'row'} gap={2} alignItems={'flex-start'}>
				<BookLangSelect label={'Translate to'} value={settings.nativeLang} options={langOptions} onChange={onChangeTranslationLang} />
				<BookVoiceSettingsHOC languageName={settings.targetLang} />
			</Stack>
			<Divider />
			<Stack
				gap={2}
				sx={regenerating ? { pointerEvents: 'none', opacity: .3 } : undefined}
			>
				<Stack gap={.5}>
					<Text variant={'subtitle2'} fontWeight={600}>Story</Text>
					<Text sx={{ color: theme.palette.grey[500] }}>Change and regenerate: the book starts over from page 1.</Text>
				</Stack>
				{/* half-width fields, lined up with the columns above */}
				<Stack direction={'row'} gap={2}>
					<BookLangSelect label={'Book language'} value={form.targetLang} options={langOptions} onChange={onChangeTargetLang} />
					<Box sx={{ flex: 1 }} />
				</Stack>
				<Stack
					direction={'row'}
					gap={2}
					alignItems={'flex-end'}
				>
					<FormControl
						sx={{
							flex: 1,
							minWidth: 0,
						}}>
						<InputLabel shrink htmlFor={'book-genre-select'}>
							{form.nonfiction ? 'Topic' : 'Genre'}
						</InputLabel>
						<Select
							id={'book-genre-select'}
							variant={'filled'}
							value={form.genre}
							sx={{
								'.MuiSelect-select': {
									fontWeight: 400 + ' !important',
								},
							}}
							onChange={(e) => set({ genre: e.target.value as string })}
						>
							{genreOptions.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
						</Select>
					</FormControl>

					<FormControlLabel
						control={
							<Check
								checked={form.nonfiction}
								onChange={onToggleNonfiction}
							/>
						}
						label={'Non-fiction'}
						sx={{
							m: 0,
							flex: 1,
							alignItems: 'center',
							minWidth: 0,
							height: theme.spacing(5),

							'.MuiFormControlLabel-label': {
								fontSize: theme.typography.subtitle2,
							},
						}}
					/>
				</Stack>
				<TextField
					fullWidth
					variant={'filled'}
					label={'In the style of'}
					value={form.styleAuthor}
					onChange={(e) => set({ styleAuthor: e.target.value })}
					InputLabelProps={{ shrink: true }}
				/>
				<TextField
					fullWidth
					multiline
					variant={'filled'}
					label={'Keywords'}
					value={form.keywords}
					onChange={(e) => set({ keywords: e.target.value })}
					InputLabelProps={{ shrink: true }}
					InputProps={{ inputComponent: 'textarea' }}
					inputProps={{ rows: 2 }}
					sx={{
						'.MuiInputBase-root.MuiInputBase-multiline': {
							p: 0,
						},
						textarea: { resize: 'vertical' },
					}}
					helperText={keywordsDropped ? `Keywords must be ${form.targetLang} words` : undefined}
				/>
				<Stack alignItems={'flex-end'} gap={.5}>
					<TextField
						fullWidth
						multiline
						minRows={3}
						maxRows={plotExpanded ? undefined : 6}
						variant={'filled'}
						label={'Plot'}
						value={form.plotSeed}
						onChange={(e) => set({ plotSeed: e.target.value })}
						InputLabelProps={{ shrink: true }}
						sx={{
							'.MuiInputBase-root.MuiInputBase-multiline': {
								p: 0,
							},
						}}
					/>
					<Button size={'small'} onClick={() => setPlotExpanded(!plotExpanded)}>
						{plotExpanded ? 'Collapse' : 'Expand'}
					</Button>
				</Stack>
				<Stack direction={'row'} justifyContent={'flex-end'}>
					<Button
						variant={'contained'}
						disabled={regenerating || !form.genre}
						onClick={() => onRegenerate({ ...form, nativeLang: settings.nativeLang })}
					>
						Regenerate
					</Button>
				</Stack>
			</Stack>
		</Stack>
	);
};
